<template>
    <div class="flex flex-col h-full">
        <slide-over-header
            class="px-6"
            :collapsed="collapsed"
            :reset-visible="false"
            :title-class-name="['!overflow-visible !whitespace-normal']"
            @close="close"
            @collapse="$emit('update:collapsed', !collapsed)"
        >
            <sms-chat-conversation-header
                :variant="headerVariant"
                v-if="selectedConversation?.id"
            />
            <span v-else>{{ t('communicationMethods.sms') }}</span>
        </slide-over-header>

        <!-- Loading State -->
        <div
            class="grow flex items-center justify-center"
            data-testid="conversation-loading"
            v-if="showLoading"
        >
            <activix-spinner class="h-10 w-10" />
        </div>

        <!-- Error State -->
        <div
            class="grow flex flex-col items-center justify-center p-4"
            data-testid="conversation-error"
            v-else-if="hasError"
        >
            <p class="text-gray-600 mb-4">
                {{ t('smsCommunication.errors.unableToLoadConversation') }}
            </p>
        </div>

        <!-- Conversation -->
        <sms-chat-conversation
            :show-conversation-details="false"
            :hide-header="true"
            v-else-if="selectedConversation?.id"
        />
    </div>
</template>

<script setup lang="ts">
    import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
    import { storeToRefs } from 'pinia';
    import { useRoute } from 'vue-router';
    import ActivixSpinner from '@/components/activix-ui/ActivixSpinner/ActivixSpinner.vue';
    import type { OpenDrawerPayload } from '@/components/sms/hooks/useSmsDrawer.ts';
    import { OpenDrawerPayloadSchema, useSmsDrawer } from '@/components/sms/hooks/useSmsDrawer.ts';
    import { useI18n } from '@/hooks/useI18n.ts';
    import { useNotify } from '@/hooks/useNotify.ts';
    import { useEventBus } from '@/hooks/useEventBus.ts';
    import { useNiotextStore } from '@/store/modules/niotext/store.ts';
    import { getSmsErrorMessage } from '@/components/sms/utils/getSmsErrorMessage.ts';
    import SlideOverHeader from '@/components/SlideOverHeader.vue';
    import SmsChatConversationHeader from '@/components/sms/SmsChatConversationHeader.vue';
    import SmsChatConversation from '@/components/sms/SmsChatConversation.vue';
    import { SidebarType } from '@/components/RightSidebar.types.ts';
    import { SmsChatConversationHeaderVariant } from '@/components/sms/SmsChatConversationHeader.types.ts';

    const route = useRoute();
    const { on, off, emit } = useEventBus();
    const {
        close,
        closeDrawerOperation,
        cleanup,
        targetCommunicationId,
        isWaitingForLeadConversation,
        clearLeadConversationOpenPreparation,
        isDrawerOperationCurrent,
        markDrawerLoadSuccess,
        markDrawerLoadFailure,
    } = useSmsDrawer();
    const { notify } = useNotify();
    const niotextStore = useNiotextStore();
    const { selectedConversation } = storeToRefs(niotextStore);

    interface Props {
        collapsed: boolean;
    }

    interface Emits {
        (e: 'update:collapsed', value: boolean): void;
    }

    const props = defineProps<Props>();
    defineEmits<Emits>();

    const { t } = useI18n();

    const isLoading = ref<boolean>(false);
    const hasError = ref<boolean>(false);

    interface ShowRightSidebarEvent {
        name: SidebarType;
        data: unknown;
    }

    const headerVariant = computed(() => {
        if (props.collapsed) {
            return SmsChatConversationHeaderVariant.SLIDEOVER_COLLAPSED;
        }

        return SmsChatConversationHeaderVariant.SLIDEOVER;
    });

    const showLoading = computed(() => isLoading.value || isWaitingForLeadConversation.value);

    const onShow = async ({ name, data = null }: ShowRightSidebarEvent): Promise<void> => {
        if (name !== SidebarType.SMS_CONVERSATION) {
            return;
        }

        if (!data) {
            return;
        }

        const result = OpenDrawerPayloadSchema.safeParse(data);

        if (!result.success) {
            hasError.value = true;
            return;
        }

        hasError.value = false;

        await initialize({ payload: data as OpenDrawerPayload });
    };

    async function initialize({ payload }: { payload: OpenDrawerPayload }): Promise<void> {
        clearLeadConversationOpenPreparation();
        isLoading.value = true;

        try {
            niotextStore.resetConversationsFetch();

            if (targetCommunicationId.value) {
                await niotextStore.openConversationFromCommunication(
                    payload.lead,
                    targetCommunicationId.value
                );
            } else if (payload.contactTarget) {
                await niotextStore.findOrCreateConversationByContact(
                    payload.lead,
                    payload.contactTarget
                );
            } else {
                if (!payload.selectedPhone) {
                    throw new Error('A lead phone is required to open an SMS conversation');
                }

                await niotextStore.findOrCreateConversationByLead(
                    payload.lead,
                    payload.selectedPhone
                );
            }

            if (!isDrawerOperationCurrent(payload.operationGeneration)) {
                return;
            }

            markDrawerLoadSuccess(payload.operationGeneration);
            emit('native-sms-drawer-loaded', { leadId: payload.lead.id });
        } catch (error: any) {
            if (!isDrawerOperationCurrent(payload.operationGeneration)) {
                return;
            }

            markDrawerLoadFailure(payload.operationGeneration, error);
            emit('native-sms-drawer-failed', { leadId: payload.lead.id, error });
            isLoading.value = false;
            closeDrawerOperation(payload.operationGeneration);
            notify.error(
                getSmsErrorMessage(
                    error,
                    t('smsCommunication.notifications.conversationLoadFailed')
                )
            );
        } finally {
            if (isDrawerOperationCurrent(payload.operationGeneration)) {
                isLoading.value = false;
            }
        }
    }

    onMounted(() => {
        on('show-right-sidebar', onShow);
    });

    onUnmounted(() => {
        off('show-right-sidebar', onShow);
        clearLeadConversationOpenPreparation();
        niotextStore.resetConversationsFetch();
        cleanup();
    });

    watch(
        () => route.name,
        (routeName) => {
            if (routeName === 'sms') {
                clearLeadConversationOpenPreparation();
                close();
                return;
            }

            if (routeName !== 'leads.update') {
                clearLeadConversationOpenPreparation();
            }
        }
    );

    watch(
        () => selectedConversation.value?.identityKey,
        (conversationIdentityKey) => {
            if (conversationIdentityKey) {
                clearLeadConversationOpenPreparation();
            }
        }
    );

    watch(
        () => isWaitingForLeadConversation.value,
        (isWaiting) => {
            if (isWaiting) {
                hasError.value = false;
            }
        }
    );
</script>
